/**
 * Mock-mode stand-in for the survey endpoints (VITE_USE_MOCK_API=true). Two
 * surveys, so the pages show how several sit side by side:
 *
 *   - Leadership Survey: the real design, a closed H1 2026 round and an open
 *     H2 2026 round still taking answers. Seven of the people asked are on
 *     the Board, each answering from their own department.
 *   - IT Team Survey: asked of IT & Data colleagues by team, with no partner
 *     teams, a choice question and a closed Q3 2026 round.
 *
 * CCD is split into Front Office (IT Commercial) and Back Office (IT SF);
 * its people choose one when they answer, mostly Back Office.
 *
 * The answers are invented, from a seeded generator so every load shows the
 * same numbers. Any link to an open round opens the Finance form (or the
 * first department's), or CCD's when the token has "ccd" in it; the token
 * "used" opens it as already answered.
 */
import type {
  SurveyAnswer,
  SurveyDepartment,
  SurveyDesign,
  SurveyForm,
  SurveyPartner,
  SurveyQuestion,
  SurveyRecipient,
  SurveyRound,
  SurveyRoundResults,
  SurveySendResult,
  SurveySubmission,
  SurveyType,
  SurveyTypeResults,
  SurveyTypeSummary,
  UserRef,
} from '../types'
import { formAreas, partnerTeam } from './surveyPartners'

const ADMIN: UserRef = { oid: 'mock-self-oid', name: 'Local Tester', email: 'local.tester@danone.com' }

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value))
const delay = <T,>(value: T, ms = 120): Promise<T> => new Promise(resolve => setTimeout(() => resolve(value), ms))
const apiError = (message: string, status: number) =>
  Object.assign(new Error(message), { status, data: { detail: message } })
const newId = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`

const question = (q: Partial<SurveyQuestion> & Pick<SurveyQuestion, 'id' | 'kind' | 'text'>): SurveyQuestion => ({
  title: '', required: true, labels: [], lowLabel: '', highLabel: '', options: [], naLabel: '', ...q,
})

// ── Leadership Survey ────────────────────────────────────────────────────────

const STARS = ['Significantly below expectations', 'Below expectations', 'Good', 'Excellent']

// In the order the email lists them.
const PARTNERS: SurveyPartner[] = [
  { id: 'p_sf', name: 'IT SF', lead: 'Johanna Lucas', team: 'Support Functions' },
  { id: 'p_com', name: 'IT Commercial', lead: 'Aneesha Patel', team: 'Commercial' },
  { id: 'p_data', name: 'Data Platforms', lead: 'Clive Ellen', team: '' },
  { id: 'p_ops', name: 'IT Operations & SPs', lead: 'Ian Jackson', team: 'Operations' },
  { id: 'p_eus', name: 'IT End User Services', lead: 'Michal Andruszkiewicz', team: 'EUS' },
]

const LEADERSHIP: SurveyDesign = {
  partners: PARTNERS,
  departments: [
    { id: 'd_sn', name: 'SN', partnerId: 'p_com', portfolio: 'SN' },
    { id: 'd_mkt', name: 'Marketing', partnerId: 'p_com', portfolio: 'Marketing' },
    {
      id: 'd_ccd', name: 'CCD', partnerId: '', portfolio: 'CCD', areas: [
        { id: 'a_front', name: 'Front Office', partnerId: 'p_com', definition: 'Working directly with customers: taking and changing orders, answering customer queries, and the tools you use to serve them day to day.' },
        { id: 'a_back', name: 'Back Office', partnerId: 'p_sf', definition: 'The processing behind the scenes: invoicing, credit and collections, customer and bank details, and the checks and reports that go with them.' },
      ],
    },
    { id: 'd_dph', name: 'Data Power House', partnerId: 'p_data', portfolio: '' },
    { id: 'd_ops', name: 'Operations', partnerId: 'p_ops', portfolio: 'CBU' },
    { id: 'd_sp', name: 'Supply Points', partnerId: 'p_ops', portfolio: 'Factory' },
    { id: 'd_fin', name: 'Finance', partnerId: 'p_sf', portfolio: 'Finance' },
    { id: 'd_dbs', name: 'DBS', partnerId: 'p_sf', portfolio: '' },
    { id: 'd_gs', name: 'GS', partnerId: 'p_sf', portfolio: 'GS' },
    { id: 'd_hr', name: 'HR & ES', partnerId: 'p_sf', portfolio: 'HR' },
  ],
  questions: [
    question({ id: 'q_overall', kind: 'rating', title: 'Overall experience', text: 'How would you rate your overall experience working with {partner}?', labels: STARS }),
    question({ id: 'q_understanding', kind: 'rating', title: 'Business understanding', text: 'How well does {partner} understand your priorities and business needs?', labels: STARS }),
    question({ id: 'q_ways', kind: 'rating', title: 'Ways of working', text: 'How effective is the way we work together?', labels: STARS }),
    question({ id: 'q_recommend', kind: 'scale', title: 'Recommend', text: 'How likely are you to recommend IT as a trusted partner to a colleague?', lowLabel: 'Not at all likely', highLabel: 'Extremely likely' }),
    question({ id: 'q_change', kind: 'text', title: 'One thing to change', text: 'One thing we should change to improve the way we work together', required: false }),
  ],
  emailSubject: 'IT & Data Functional Portfolio survey',
  emailBody: [
    'Thank you for taking the time to participate in this survey. We would love to hear your thoughts on IT & DATA Functional Portfolio services provided in 2026. This survey aims to gather insights into the portfolio services and engagement effectiveness, and identify any areas to focus on the first half of 2027.',
    'Your key technology business partners are:\n{partners}',
  ].join('\n\n'),
  partnerFallback: 'IT',
}

const LEADERSHIP_INVITED: Record<string, number> = {
  d_sn: 2, d_mkt: 3, d_ccd: 4, d_dph: 1, d_ops: 3, d_sp: 3,
  d_fin: 5, d_dbs: 2, d_gs: 1, d_hr: 2,
}
// Of those, the Board members: the last of each department's people.
const LEADERSHIP_BOARD: Record<string, number> = { d_mkt: 1, d_ccd: 1, d_ops: 1, d_sp: 1, d_fin: 1, d_gs: 1, d_hr: 1 }
// Where each partner tends to land (1-4), so the teams differ.
const LEADERSHIP_LEVEL: Record<string, number> = { p_com: 2.3, p_data: 2.9, p_ops: 2.6, p_sf: 2.8 }
const LEADERSHIP_COMMENTS = [
  'Earlier sight of the roadmap so we can plan around releases.',
  'A single point of contact for small requests.',
  'Fewer status meetings, more working sessions.',
  'Share what has been delivered each quarter, not just what is in flight.',
  'Faster turnaround on access requests for new starters.',
  'Involve us before the solution is chosen, not after.',
  'Clearer prioritisation when requests compete.',
  'More time on site with the teams who use the tools.',
]

// ── IT Team Survey ───────────────────────────────────────────────────────────

const IT_TEAM: SurveyDesign = {
  partners: [],
  departments: [
    { id: 'd_t_sf', name: 'Support Functions', partnerId: '', portfolio: '' },
    { id: 'd_t_com', name: 'Commercial', partnerId: '', portfolio: '' },
    { id: 'd_t_ops', name: 'Operations', partnerId: '', portfolio: '' },
    { id: 'd_t_infra', name: 'Infrastructure', partnerId: '', portfolio: '' },
    { id: 'd_t_eus', name: 'EUS', partnerId: '', portfolio: '' },
  ],
  questions: [
    question({ id: 'q_priorities', kind: 'rating', title: 'Clear priorities', text: 'How clear are the priorities for your team over the next six months?', labels: ['Not at all clear', 'Somewhat unclear', 'Clear', 'Very clear'] }),
    question({ id: 'q_together', kind: 'rating', title: 'Working together', text: 'How well do the teams across IT & Data work together?', labels: ['Very poorly', 'Poorly', 'Well', 'Very well'] }),
    question({ id: 'q_tools', kind: 'choice', title: 'Tools', text: 'Do you have the tools you need to do your job well?', options: ['Yes', 'Partly', 'No'] }),
    question({ id: 'q_place', kind: 'scale', title: 'Recommend', text: 'How likely are you to recommend IT & Data as a place to work?', lowLabel: 'Not at all likely', highLabel: 'Extremely likely' }),
    question({ id: 'q_easier', kind: 'text', title: 'One thing to change', text: 'One thing that would make your work easier', required: false }),
  ],
  emailSubject: 'IT & Data team survey',
  emailBody: 'Five questions, two minutes. Tell us how the quarter has gone for you and your team.',
  partnerFallback: 'IT',
}

const IT_TEAM_INVITED: Record<string, number> = { d_t_sf: 8, d_t_com: 7, d_t_ops: 9, d_t_infra: 5, d_t_eus: 6 }
const IT_TEAM_LEVEL: Record<string, number> = { d_t_sf: 2.9, d_t_com: 2.5, d_t_ops: 2.3, d_t_infra: 3.0, d_t_eus: 2.6 }
const IT_TEAM_COMMENTS = [
  'Fewer tools doing the same job.',
  'Clearer ownership between teams.',
  'Time to fix things properly, not just patch them.',
  'Better onboarding for new joiners.',
  'Share the roadmap with the whole team, not just leads.',
  'Fewer last-minute requests.',
]

// ── Answers ──────────────────────────────────────────────────────────────────

// mulberry32: small, seeded, good enough for test data.
function seeded(seed: number) {
  let a = seed
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function generate(opts: {
  seed: number
  design: SurveyDesign
  invited: Record<string, number>
  board?: Record<string, number>
  level: (d: SurveyDepartment, partnerId: string) => number
  comments: string[]
  share: number
  firstDay: string
}): SurveySubmission[] {
  const rand = seeded(opts.seed)
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.round(v)))
  const out: SurveySubmission[] = []
  for (const d of opts.design.departments) {
    const invited = opts.invited[d.id] ?? 0
    for (let i = 0; i < invited; i++) {
      if (rand() > opts.share) continue
      const board = i >= invited - (opts.board?.[d.id] ?? 0)
      // In a split department, two in three choose its last area (CCD Back
      // Office, as IT SF does most of CCD's work).
      const area = d.areas?.length ? d.areas[rand() < 0.35 ? 0 : d.areas.length - 1] : undefined
      const partnerId = area?.partnerId ?? d.partnerId
      // The Board a touch more generous than the rest, so the two differ.
      const level = opts.level(d, partnerId) + (board ? 0.35 : 0)
      const answers: Record<string, SurveyAnswer> = {}
      for (const q of opts.design.questions) {
        if (q.naLabel && rand() < 0.2) { answers[q.id] = 'na'; continue }
        if (q.kind === 'rating') answers[q.id] = clamp(level + (rand() - 0.5) * 2.4, 1, q.labels.length)
        else if (q.kind === 'scale') answers[q.id] = clamp(level * 2.2 + 1.8 + (rand() - 0.5) * 4, 0, 10)
        else if (q.kind === 'choice') { const r = rand(); answers[q.id] = q.options[r < 0.5 ? 0 : r < 0.85 ? 1 : 2] }
        else answers[q.id] = rand() < 0.6 ? opts.comments[Math.floor(rand() * opts.comments.length)] : null
      }
      const day = new Date(opts.firstDay)
      day.setUTCDate(day.getUTCDate() + Math.floor(rand() * 9))
      out.push({ id: `sub_${opts.seed}_${out.length}`, departmentId: d.id, partnerId, area: area?.id ?? '', board, answers, submittedOn: day.toISOString().slice(0, 10) })
    }
  }
  return out
}

const counted = (subs: SurveySubmission[]) =>
  subs.reduce<Record<string, number>>((acc, s) => ({ ...acc, [s.departmentId]: (acc[s.departmentId] ?? 0) + 1 }), {})

/** People sent their link, by department: everyone, then the Board. */
const invitedBy = (recipients: SurveyRecipient[], board: boolean) =>
  recipients.filter(p => p.invitedAt && (!board || p.board))
    .reduce<Record<string, number>>((acc, p) => ({ ...acc, [p.departmentId]: (acc[p.departmentId] ?? 0) + 1 }), {})

// Invented people. Sam Taylor in Finance is the one the walkthrough follows.
const FIRST = ['Sam', 'Alex', 'Hannah', 'Daniel', 'Olivia', 'Ben', 'Grace', 'Ryan', 'Emma', 'Liam', 'Sophie', 'Matt', 'Chloe',
  'Nathan', 'Isla', 'Owen', 'Ruby', 'Adam', 'Zoe', 'Luke', 'Mia', 'Jack', 'Ella', 'Noah', 'Lucy', 'Ethan', 'Amy', 'Sean', 'Kate', 'Lars', 'Anna', 'Marco']
const LAST = ['Taylor', 'Morgan', 'Lewis', 'Price', 'Turner', 'Walsh', 'Hughes', 'Murphy', 'Collins', 'Doyle', 'Ward', 'Fisher',
  'Barnes', 'Hill', 'Grant', 'Kelly', 'Shaw', 'Ross', 'Bennett', 'Harper', 'Foster', 'Wilson', 'Ryan', 'Parker', 'Hart', 'Cole', 'Berg', 'Lind', 'Rossi']

const people = (design: SurveyDesign, invited: Record<string, number>, board: Record<string, number>, invitedAt: string | null, offset = 0): SurveyRecipient[] => {
  const finance = design.departments.findIndex(d => d.name === 'Finance')
  const order = finance > 0 ? [design.departments[finance], ...design.departments.filter((_, i) => i !== finance)] : design.departments
  let n = offset
  const byDepartment = new Map(order.map(d => [d.id, Array.from({ length: invited[d.id] ?? 0 }, (_, i) => {
    const first = FIRST[n % FIRST.length]
    const last = LAST[(n * 7) % LAST.length]
    n++
    const onBoard = i >= (invited[d.id] ?? 0) - (board[d.id] ?? 0)
    return { name: `${first} ${last}`, email: `${first}.${last}@danone.com`.toLowerCase(), departmentId: d.id, board: onBoard, invitedAt }
  })]))
  return design.departments.flatMap(d => byDepartment.get(d.id) ?? [])
}

const leadershipLevel = (_d: SurveyDepartment, partnerId: string) => LEADERSHIP_LEVEL[partnerId] ?? 3
const h1 = generate({ seed: 11, design: LEADERSHIP, invited: LEADERSHIP_INVITED, board: LEADERSHIP_BOARD, level: leadershipLevel, comments: LEADERSHIP_COMMENTS, share: 0.85, firstDay: '2026-04-14' })
const h2 = generate({ seed: 29, design: LEADERSHIP, invited: LEADERSHIP_INVITED, board: LEADERSHIP_BOARD, level: (d, p) => leadershipLevel(d, p) + 0.15, comments: LEADERSHIP_COMMENTS, share: 0.55, firstDay: '2026-10-13' })
const q3 = generate({ seed: 7, design: IT_TEAM, invited: IT_TEAM_INVITED, level: d => IT_TEAM_LEVEL[d.id] ?? 3, comments: IT_TEAM_COMMENTS, share: 0.75, firstDay: '2026-09-08' })

type RoundSeed = Pick<SurveyRound, 'id' | 'name' | 'status'> & Partial<SurveyRound>

function round(type: SurveyType, invited: Record<string, number>, board: Record<string, number>, seed: RoundSeed, subs: SurveySubmission[] = []): SurveyRound {
  const sent = seed.status !== 'draft'
  const recipients = people(type, invited, board, sent ? seed.sentAt ?? null : null, type.id === 'st_it_team' ? 11 : 0)
  return {
    typeId: type.id, typeName: type.name, ...clone(designOf(type)),
    recipients,
    invited: invitedBy(recipients, false), responded: counted(subs),
    invitedBoard: invitedBy(recipients, true), respondedBoard: counted(subs.filter(s => s.board)),
    sentAt: null, sentBy: sent ? ADMIN : null, reminders: [], closedAt: null, closedBy: null,
    ...seed,
  } as SurveyRound
}

const designOf = (d: SurveyDesign): SurveyDesign => ({
  partners: d.partners, departments: d.departments, questions: d.questions,
  emailSubject: d.emailSubject, emailBody: d.emailBody, partnerFallback: d.partnerFallback,
})

const leadershipType: SurveyType = {
  id: 'st_leadership', name: 'Leadership Survey',
  description: 'IT & Data Functional Portfolio services, asked of business leaders twice a year.',
  ...clone(LEADERSHIP), createdAt: '2026-03-30T09:00:00Z', createdBy: ADMIN,
}
const itTeamType: SurveyType = {
  id: 'st_it_team', name: 'IT Team Survey',
  description: 'IT & Data colleagues, once a quarter.',
  ...clone(IT_TEAM), createdAt: '2026-08-20T09:00:00Z', createdBy: ADMIN,
}

const state = {
  types: [leadershipType, itTeamType],
  rounds: [
    round(leadershipType, LEADERSHIP_INVITED, LEADERSHIP_BOARD, {
      id: 'sr_h2_2026', name: 'H2 2026', status: 'open', sentAt: '2026-10-13T08:00:00Z',
      reminders: [{ at: '2026-10-20T08:00:00Z', by: ADMIN, sent: 26 }], createdAt: '2026-10-09T09:00:00Z',
    }, h2),
    round(leadershipType, LEADERSHIP_INVITED, LEADERSHIP_BOARD, {
      id: 'sr_h1_2026', name: 'H1 2026', status: 'closed', sentAt: '2026-04-14T08:00:00Z',
      reminders: [{ at: '2026-04-24T08:00:00Z', by: ADMIN, sent: 26 }],
      closedAt: '2026-05-02T16:00:00Z', closedBy: ADMIN, createdAt: '2026-04-10T09:00:00Z',
    }, h1),
    round(itTeamType, IT_TEAM_INVITED, {}, {
      id: 'sr_q4_2026', name: 'Q4 2026', status: 'draft', createdAt: '2026-10-18T09:00:00Z',
    }),
    round(itTeamType, IT_TEAM_INVITED, {}, {
      id: 'sr_q3_2026', name: 'Q3 2026', status: 'closed', sentAt: '2026-09-08T08:00:00Z',
      closedAt: '2026-09-26T16:00:00Z', closedBy: ADMIN, createdAt: '2026-09-02T09:00:00Z',
    }, q3),
  ],
  submissions: { sr_h1_2026: h1, sr_h2_2026: h2, sr_q3_2026: q3 } as Record<string, SurveySubmission[]>,
}

const findRound = (id: string) => {
  const found = state.rounds.find(r => r.id === id)
  if (!found) throw apiError('Round not found', 404)
  return found
}

/** Whose link a mock token is: CCD's when it says so, otherwise Finance's. */
const linkDepartment = (r: SurveyRound, token: string): SurveyDepartment => {
  const ccd = token.includes('ccd') ? r.departments.find(d => d.areas?.length) : undefined
  return ccd ?? r.departments.find(d => d.name === 'Finance') ?? r.departments.find(d => d.partnerId) ?? r.departments[0]
}

const withIds = (body: SurveyDesign): SurveyDesign => ({
  ...body,
  partners: body.partners.map(p => ({ ...p, id: p.id || newId('p') })),
  departments: body.departments.map(d => ({ ...d, id: d.id || newId('d') })),
  questions: body.questions.map(q => ({ ...q, id: q.id || newId('q') })),
})

export function createMockSurveyApi() {
  return {
    getSurveyTypes: (): Promise<SurveyType[]> => delay(clone(state.types)),
    createSurveyType: (body: SurveyDesign & { name: string; description: string }): Promise<SurveyType> => {
      const created: SurveyType = { ...clone(withIds(body)), name: body.name, description: body.description, id: newId('st'), createdAt: new Date().toISOString(), createdBy: ADMIN }
      state.types.push(created)
      return delay(clone(created))
    },
    updateSurveyType: (id: string, body: SurveyDesign & { name: string; description: string }): Promise<SurveyType> => {
      const index = state.types.findIndex(t => t.id === id)
      if (index < 0) return Promise.reject(apiError('Survey not found', 404))
      const design = withIds(body)
      state.types[index] = { ...state.types[index], ...clone(design), name: body.name, description: body.description, updatedAt: new Date().toISOString(), updatedBy: ADMIN }
      // Drafts follow the survey; sent rounds keep what they were sent with.
      for (const r of state.rounds) {
        if (r.typeId === id && r.status === 'draft') Object.assign(r, clone(designOf(design)), { typeName: body.name })
      }
      return delay(clone(state.types[index]))
    },
    deleteSurveyType: (id: string) => {
      if (state.rounds.some(r => r.typeId === id)) return Promise.reject(apiError("This survey has rounds, so it can't be deleted.", 400))
      state.types = state.types.filter(t => t.id !== id)
      return delay(null) as Promise<any>
    },
    getSurveyRounds: (typeId?: string): Promise<SurveyRound[]> =>
      delay(clone(state.rounds.filter(r => !typeId || r.typeId === typeId))),
    createSurveyRound: (body: { typeId: string; name: string; recipientsFrom?: string }): Promise<SurveyRound> => {
      const type = state.types.find(t => t.id === body.typeId)
      if (!type) return Promise.reject(apiError('Survey not found', 404))
      const from = body.recipientsFrom ? state.rounds.find(r => r.id === body.recipientsFrom) : undefined
      const created: SurveyRound = {
        id: newId('sr'), typeId: type.id, typeName: type.name, name: body.name, status: 'draft',
        ...clone(designOf(type)),
        recipients: (from?.recipients ?? []).map(r => ({ ...r, invitedAt: null })),
        invited: {}, responded: {}, invitedBoard: {}, respondedBoard: {}, reminders: [], createdAt: new Date().toISOString(),
      }
      state.rounds.unshift(created)
      return delay(clone(created))
    },
    updateSurveyRound: (id: string, body: SurveyDesign & { name: string; recipients: SurveyRecipient[] }): Promise<SurveyRound> => {
      const r = findRound(id)
      if (r.status === 'closed') return Promise.reject(apiError('This round is closed. Reopen it to change it.', 400))
      const invitedAt = new Map(r.recipients.map(p => [p.email.toLowerCase(), p.invitedAt]))
      r.name = body.name
      r.recipients = body.recipients.map(p => ({ ...p, board: !!p.board, invitedAt: invitedAt.get(p.email.toLowerCase()) ?? null }))
      r.invited = invitedBy(r.recipients, false)
      r.invitedBoard = invitedBy(r.recipients, true)
      return delay(clone(r))
    },
    deleteSurveyRound: (id: string) => {
      if (findRound(id).status !== 'draft') return Promise.reject(apiError("Only a round that hasn't been sent can be deleted.", 400))
      state.rounds = state.rounds.filter(r => r.id !== id)
      return delay(null) as Promise<any>
    },
    sendSurveyRound: (id: string, _origin: string): Promise<SurveySendResult> => {
      const r = findRound(id)
      const now = new Date().toISOString()
      const pending = r.recipients.filter(p => !p.invitedAt)
      if (!pending.length) return Promise.reject(apiError('Everyone on this round has already been sent their link.', 400))
      for (const p of pending) p.invitedAt = now
      r.invited = invitedBy(r.recipients, false)
      r.invitedBoard = invitedBy(r.recipients, true)
      if (r.status === 'draft') Object.assign(r, { status: 'open', sentAt: now, sentBy: ADMIN })
      return delay({ sent: pending.map(p => p.email), failed: [], round: clone(r) })
    },
    remindSurveyRound: (id: string, _origin: string): Promise<SurveySendResult> => {
      const r = findRound(id)
      const invited = r.recipients.filter(p => p.invitedAt)
      r.reminders.push({ at: new Date().toISOString(), by: ADMIN, sent: invited.length })
      return delay({ sent: invited.map(p => p.email), failed: [], round: clone(r) })
    },
    closeSurveyRound: (id: string): Promise<SurveyRound> => {
      const r = findRound(id)
      Object.assign(r, { status: 'closed', closedAt: new Date().toISOString(), closedBy: ADMIN })
      return delay(clone(r))
    },
    reopenSurveyRound: (id: string): Promise<SurveyRound> => {
      const r = findRound(id)
      Object.assign(r, { status: 'open', closedAt: null, closedBy: null })
      return delay(clone(r))
    },
    getSurveyResultTypes: (): Promise<SurveyTypeSummary[]> =>
      delay(state.types.map(t => ({
        id: t.id, name: t.name, description: t.description,
        rounds: state.rounds.filter(r => r.typeId === t.id && r.status !== 'draft').length,
      }))),
    getSurveyResults: (typeId: string): Promise<SurveyTypeResults> => {
      const type = state.types.find(t => t.id === typeId)
      if (!type) return Promise.reject(apiError('Survey not found', 404))
      const rounds: SurveyRoundResults[] = state.rounds
        .filter(r => r.typeId === typeId && r.status !== 'draft')
        .sort((a, b) => (a.sentAt ?? '').localeCompare(b.sentAt ?? ''))
        .map(r => ({
          id: r.id, name: r.name, status: r.status, sentAt: r.sentAt, closedAt: r.closedAt,
          ...clone(designOf(r)), invited: { ...r.invited }, responded: { ...r.responded },
          invitedBoard: { ...r.invitedBoard }, respondedBoard: { ...r.respondedBoard },
          submissions: clone(state.submissions[r.id] ?? []),
        }))
      return delay({ id: type.id, name: type.name, description: type.description, rounds })
    },
    getSurveyForm: (roundId: string, token: string): Promise<SurveyForm | null> => {
      const r = state.rounds.find(x => x.id === roundId && x.status !== 'draft')
      if (!r) return delay(null)
      const department = linkDepartment(r, token)
      const areas = formAreas(r, department?.id ?? '')
      const team = areas.length ? { team: '', supports: [], partner: '' } : partnerTeam(r, department?.id ?? '')
      return delay({
        roundName: r.name, typeName: r.typeName, status: r.status, submitted: token === 'used',
        department: department?.name ?? '', ...team, areas, questions: r.questions,
      })
    },
    submitSurveyAnswers: (roundId: string, token: string, answers: Record<string, SurveyAnswer>, area?: string) => {
      const r = findRound(roundId)
      if (r.status !== 'open') return Promise.reject(Object.assign(apiError('closed', 410), { data: { error: 'closed' } }))
      const department = linkDepartment(r, token)
      const chosen = department.areas?.find(a => a.id === area)
      if (department.areas?.length && !chosen) return Promise.reject(apiError("Choose the area you're giving feedback on.", 400))
      const list = state.submissions[roundId] ?? (state.submissions[roundId] = [])
      list.push({ id: newId('sub'), departmentId: department.id, partnerId: chosen?.partnerId ?? department.partnerId, area: chosen?.id ?? '', board: false, answers: clone(answers), submittedOn: new Date().toISOString().slice(0, 10) })
      r.responded[department.id] = (r.responded[department.id] ?? 0) + 1
      return delay({ submitted: true }) as Promise<any>
    },
  }
}
